import { memo, useState } from "react";

import { NavLink, Outlet, useMatch } from "react-router-dom";

import { Avatar } from "@/components/ui/avatar";
import { FeedbackModal } from "@/components/ui/feedback-modal";
import { Toaster } from "@/components/ui/toaster";
import { Badge, Box, Button, Collapsible, Flex, Group, Heading, Menu, Portal, Separator, Spinner, Text } from "@chakra-ui/react";

import { FaRegPaperPlane } from "react-icons/fa";
import { LuBuilding2, LuChevronDown, LuCompass, LuLogOut, LuMenu, LuMessageCircle, LuPlane, LuSettings, LuSparkles, LuX } from "react-icons/lu";

import { useAuth } from "@/AuthContext";
import { useWorkspace } from "@/hooks/useWorkspace";
import { pickPalette } from "@utils/avatar";

const MobileNavItem = ({ to, isActive, icon, label, onClick }) => (
  <Button
    as={NavLink}
    to={to}
    size="lg"
    variant="ghost"
    justifyContent="start"
    w="full"
    onClick={onClick}
    borderRadius="xl"
    fontWeight={isActive ? "600" : "500"}
    color={isActive ? "gray.900" : "gray.500"}
    bg={isActive ? "gray.100" : "transparent"}>
    {icon} {label}
  </Button>
);

const NavigationItems = memo(({ tripsMatch, experiencesMatch, agencyMatch, exploreMatch, agencyLink, onClick }) => {
  //Hide agency nav if user is not an agency member (e.g. they are just browsing the directory)
  const { isAgencyMember, agencyId } = useWorkspace();

  return (
    <Flex direction="column" gap={0.5}>
      <MobileNavItem to="/trips" isActive={tripsMatch} icon={<LuPlane />} label="Trips" onClick={onClick} />
      <MobileNavItem to="/experiences" isActive={experiencesMatch} icon={<LuCompass />} label="Experiences" onClick={onClick} />
      {isAgencyMember && <MobileNavItem to={agencyLink} isActive={agencyMatch} icon={<LuBuilding2 />} label="Travel Advisors" onClick={onClick} />}
      <Separator my={2} borderColor="gray.200" />
      <FeedbackModal>
        <Button size="lg" variant="ghost" justifyContent="start" w="full" onClick={onClick} borderRadius="xl" fontWeight="500" color="gray.500" _hover={{ bg: "gray.100", color: "gray.900" }} transition="all 0.2s">
          <LuMessageCircle /> Support & Feedback
        </Button>
      </FeedbackModal>
    </Flex>
  );
});

const NavPill = ({ to, isActive, icon, label }) => (
  <Button
    as={NavLink}
    to={to}
    variant="ghost"
    size="sm"
    borderRadius="full"
    fontWeight={isActive ? "600" : "500"}
    fontSize="sm"
    color={isActive ? "gray.900" : "gray.500"}
    bg={isActive ? "white" : "transparent"}
    boxShadow={isActive ? "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)" : "none"}
    _hover={{ bg: isActive ? "white" : "gray.200/60", color: "gray.900" }}
    transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
    px={4}>
    {icon} {label}
  </Button>
);

const HeaderNavigation = memo(({ tripsMatch, experiencesMatch, exploreMatch, agencyMatch, agencyLink }) => {
  //Hide agency nav if user is not an agency member (e.g. they are just browsing the directory)
  const { isAgencyMember, agencyId } = useWorkspace();
  return (
    <Group gap={3} bg="gray.100" borderRadius="full" p={1}>
      <NavPill to="/trips" isActive={tripsMatch} icon={<LuPlane size={15} />} label="Trips" />
      <NavPill to="/experiences" isActive={experiencesMatch} icon={<LuCompass size={15} />} label="Experiences" />
      {isAgencyMember && <NavPill to={agencyLink} isActive={agencyMatch} icon={<LuBuilding2 size={15} />} label="Travel Advisors" />}
    </Group>
  );
});

const Me = memo(() => {
  const { user, isLoading, signOut } = useAuth();
  const { isAgencyMember, agencyId } = useWorkspace();
  return (
    <Menu.Root>
      <Menu.Trigger>
        <Flex direction="row" align="center" gap={1.5} cursor="pointer" p={1} pr={2.5} borderRadius="full" transition="all 0.2s" bg="gray.50" _hover={{ bg: "gray.100" }} borderWidth="1px" borderColor="gray.200">
          {isLoading && <Spinner size="sm" />}
          {user && (
            <Box position="relative">
              <Avatar size="sm" src={user.asset?.url} name={user.name} colorPalette={pickPalette(user.name)} borderWidth="2px" borderColor={user.premium ? "green.500" : "gray.200"} />
              {user.premium && (
                <Flex position="absolute" bottom={-1} right={-1} w={4} h={4} bg="green.500" borderRadius="full" align="center" justify="center" borderWidth="2px" borderColor="white">
                  <LuSparkles size={8} color="white" />
                </Flex>
              )}
            </Box>
          )}
          {user && <LuChevronDown size={14} color="var(--chakra-colors-gray-500)" />}
        </Flex>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content minW="200px" borderRadius="xl" boxShadow="0 8px 30px rgba(0,0,0,0.12)" border="1px solid" borderColor="gray.100" p={1}>
            <Box px={3} py={2} mb={1}>
              <Text fontWeight="600" color="gray.900" fontSize="sm">
                {user?.name}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {user?.email}
              </Text>
              {user?.premium && (
                <Badge size="sm" colorPalette="green" variant="subtle" mt={1.5} borderRadius="md">
                  Pro Member
                </Badge>
              )}
            </Box>
            {/* {isAgencyMember && (
              <Box m={2}>
                <WorkspaceSwitcher />
              </Box>
            )} */}
            <Separator />
            <Menu.Item value="settings" cursor="pointer" asChild borderRadius="lg" my={1} _hover={{ bg: "gray.50" }}>
              <NavLink to="/settings">
                <LuSettings size={16} />
                <Text>Settings</Text>
              </NavLink>
            </Menu.Item>
            {!isAgencyMember && (
              <Menu.Item value="agency" cursor="pointer" asChild borderRadius="lg" my={1} _hover={{ bg: "gray.50" }}>
                <NavLink to="/agency/signup">
                  <LuBuilding2 size={16} />
                  <Text>Create Travel Agency</Text>
                </NavLink>
              </Menu.Item>
            )}
            <Menu.Item value="signout" onClick={signOut} cursor="pointer" borderRadius="lg" color="red.600" _hover={{ bg: "red.50" }}>
              <LuLogOut size={16} />
              <Text>Sign Out</Text>
            </Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
});

const Layout = () => {
  const { user, isLoading } = useAuth();
  const { isAgencyMember, agencyId } = useWorkspace();
  const [open, setOpen] = useState(false);
  const tripsMatch = useMatch("/trips/*");
  const experiencesMatch = useMatch("/experiences/*");
  const exploreMatch = useMatch("/explore/*");
  const agencyMatch = useMatch("/agency/*");
  const agencyLink = isAgencyMember ? "/agency/dashboard" : "/agency/directory";

  const toggleMenu = () => {
    window.scrollTo(0, 0);
    setOpen(!open);
  };

  return (
    <Flex direction="column" minH="100vh" bg="gray.50">
      <Flex position="sticky" top={0} direction="column" bg="white/90" backdropFilter="blur(16px) saturate(180%)" w="full" borderBottomWidth="1px" borderBottomColor="gray.200/80" px={{ base: 3, md: 5 }} py={2.5} zIndex="10">
        <Flex direction="row" align="center" justify="space-between" gap={3}>
          <Button variant="ghost" size="sm" display={{ base: "flex", md: "none" }} onClick={toggleMenu} borderRadius="lg" color="gray.700" _hover={{ bg: "gray.100" }}>
            {open ? <LuX size={20} /> : <LuMenu size={20} />}
          </Button>

          <Flex
            direction="row"
            align="center"
            gap={2.5}
            as={NavLink}
            to={user ? "/trips" : "https://www.planharmony.com?utm_source=public_trip&utm_medium=logo"}
            _hover={{ textDecoration: "none" }}
            transition="opacity 0.2s"
            _active={{ opacity: 0.7 }}>
            <FaRegPaperPlane size="25" />
            <Heading fontSize="xl" fontWeight="700" color="gray.900" letterSpacing="-0.02em" display={{ base: "none", sm: "block" }}>
              Plan Harmony
            </Heading>
          </Flex>

          <Flex direction="row" flex={1} justify="center" display={{ base: "none", md: "flex" }}>
            {user && <HeaderNavigation tripsMatch={tripsMatch} experiencesMatch={experiencesMatch} agencyMatch={agencyMatch} agencyLink={agencyLink} />}
          </Flex>

          {isLoading && <Spinner size="sm" color="gray.400" />}
          {user && (
            <Flex direction="row" justify="end" align="center" gap={2}>
              <FeedbackModal>
                <Button variant="ghost" display={{ base: "none", md: "flex" }} borderRadius="xl" color="gray.600">
                  <LuMessageCircle size={16} /> Feedback
                </Button>
              </FeedbackModal>
              <Me />
            </Flex>
          )}
        </Flex>
      </Flex>

      <Collapsible.Root open={open} onOpenChange={toggleMenu}>
        <Collapsible.Content>
          <Flex direction="column" w="full" bg="white" borderBottomWidth="1px" borderBottomColor="gray.200/80" p={4} pb={5} boxShadow="0 8px 24px rgba(0,0,0,0.06)">
            {user && <NavigationItems tripsMatch={tripsMatch} experiencesMatch={experiencesMatch} agencyMatch={agencyMatch} agencyLink={agencyLink} onClick={toggleMenu} />}
          </Flex>
        </Collapsible.Content>
      </Collapsible.Root>

      <Box flex={1} p={{ base: 3, md: 4 }} w="full" mx="auto">
        <Outlet />
      </Box>

      <Toaster />
    </Flex>
  );
};

export default Layout;
